import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/admin/lib/api";
import { useOrg } from "@/admin/hooks/use-org";

export interface FormQuestionRef {
  code: string;
  required: boolean;
  /** Shown only when an earlier question has one of these answers. */
  visible_when?: Record<string, string[]>;
}

export interface FormTemplate {
  uuid: string;
  key: string;
  name: string;
  description: string;
  questions: FormQuestionRef[];
  record_types: string[];
  is_active: boolean;
  version: number;
  updated_at: string;
}

export interface FormQuestion {
  uuid: string;
  code_name: string;
  display_name: string;
  description?: string;
  field_type: string;
  required: boolean;
  hint?: string;
  options?: Array<{ value: string; label: string }>;
  configuration_data?: Record<string, unknown>;
}

export type FormRequestStatus = "sent" | "submitted" | "expired" | "cancelled";

export interface FormRequest {
  uuid: string;
  template_key: string;
  template_name: string;
  record_type: string;
  record_uuid: string;
  status: FormRequestStatus;
  recipient_name: string;
  recipient_email: string;
  sent_at: string;
  expires_at: string | null;
  submitted_at: string | null;
  cancelled_at: string | null;
  questions: FormQuestion[];
  answers: Record<string, unknown>;
}

export type FormTemplateWrite = Partial<
  Pick<FormTemplate, "name" | "description" | "questions" | "record_types" | "is_active">
> & { key: string };

/**
 * Templates in this org; `recordType` narrows to the ones sendable from it.
 * Forms are internal only, so partner sessions never ask.
 */
export function useFormTemplates(
  opts: { recordType?: string; activeOnly?: boolean } = {},
) {
  const { isPartnerAccess } = useOrg();
  const params = {
    ...(opts.recordType ? { record_type: opts.recordType } : {}),
    ...(opts.activeOnly ? { active: "true" } : {}),
  };
  return useQuery({
    queryKey: ["form-templates", params],
    queryFn: async () =>
      (await api.get<FormTemplate[]>("/forms/templates/", { params })).data ?? [],
    enabled: !isPartnerAccess,
  });
}

export function useSaveFormTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ isNew, ...payload }: FormTemplateWrite & { isNew: boolean }) =>
      isNew
        ? (await api.post<FormTemplate>("/forms/templates/", payload)).data
        : (await api.patch<FormTemplate>(`/forms/templates/${payload.key}/`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["form-templates"] }),
  });
}

/** Forms sent about one record, with their answers. */
export function useRecordForms(recordType: string, recordUuid: string | undefined) {
  const { isPartnerAccess } = useOrg();
  return useQuery({
    queryKey: ["form-requests", recordType, recordUuid],
    queryFn: async () =>
      (
        await api.get<FormRequest[]>("/forms/requests/", {
          params: { record_type: recordType, record_uuid: recordUuid },
        })
      ).data ?? [],
    enabled: !!recordUuid && !isPartnerAccess,
  });
}

function useFormRequestMutation<V>(fn: (vars: V) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["form-requests"] });
      qc.invalidateQueries({ queryKey: ["communications-activity"] });
    },
  });
}

export function useSendForm() {
  return useFormRequestMutation(
    async (payload: {
      template_key: string;
      record_type: string;
      record_uuid: string;
      recipient_email: string;
      recipient_name?: string;
    }) => (await api.post<FormRequest>("/forms/requests/", payload)).data,
  );
}

export function useResendForm() {
  return useFormRequestMutation(
    async (uuid: string) =>
      (await api.post<FormRequest>(`/forms/requests/${uuid}/resend/`)).data,
  );
}

export function useCancelForm() {
  return useFormRequestMutation(
    async (uuid: string) =>
      (await api.post<FormRequest>(`/forms/requests/${uuid}/cancel/`)).data,
  );
}
